/**
 * DefinitionPicker — search + multi-select for definition-driven fields
 * (Ürün Kategorileri, Markalar, Kumaş Çeşitleri, Aksesuar Çeşitleri).
 *
 * Data source: `/api/definitions/{kind}`. Automatically reloads on mount.
 * Includes an inline "+ Yeni Ekle" button that opens a mini dialog to POST
 * a new definition — the created item is auto-selected.
 *
 * Props:
 *   kind         : "brand" | "product_category" | "fabric_type" | "accessory_category"
 *   label        : Turkish label shown as header (e.g. "Markalar")
 *   value        : string[]  — selected NAMES (definition items are keyed by name)
 *   onChange     : (names: string[]) => void
 *   testIdPrefix : string
 *   placeholder  : string
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, X, Check, Plus, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

export default function DefinitionPicker({
  kind,
  label,
  value = [],
  onChange,
  testIdPrefix,
  placeholder = "İsimle ara…",
  sourceUrl,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const prefix = testIdPrefix || `def-picker-${kind}`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(sourceUrl || `/definitions/${kind}`);
      setItems((data || []).filter((d) => d.is_active !== false));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const selected = useMemo(
    () => items.filter((it) => value.includes(it.name)),
    [items, value]
  );

  // Case-insensitive with Turkish locale awareness.
  const normalize = (s) => (s || "").toLocaleLowerCase("tr-TR");

  const filtered = useMemo(() => {
    const term = normalize(q.trim());
    if (!term) return items.slice(0, 40);
    return items.filter((it) => matchesAll(term, [it.name])).slice(0, 40);
  }, [items, q]);

  const toggle = (name) => {
    if (value.includes(name)) onChange(value.filter((v) => v !== name));
    else onChange([...value, name]);
  };
  const remove = (name) => onChange(value.filter((v) => v !== name));

  const createNew = async () => {
    const nm = newName.trim();
    if (!nm) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/definitions/${kind}`, { name: nm });
      toast.success(`"${data.name}" eklendi`);
      setNewName("");
      setAddOpen(false);
      await load();
      // Auto-select the newly created entry
      if (!value.includes(data.name)) onChange([...value, data.name]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={prefix}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
          {label}
          {selected.length > 0 && (
            <span className="ml-2 text-blue-400">({selected.length} seçili)</span>
          )}
        </Label>
        <button
          type="button"
          data-testid={`${prefix}-new-btn`}
          onClick={() => setAddOpen(true)}
          className="text-[11px] font-mono uppercase text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 transition-colors"
        >
          <Plus className="w-3 h-3" /> Yeni Ekle
        </button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((it) => (
            <span
              key={it.id}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-emerald-900/60 text-emerald-200 bg-emerald-950/40 rounded-sm font-mono"
              data-testid={`${prefix}-chip-${it.name}`}
            >
              <span className="max-w-[140px] truncate">{it.name}</span>
              <button
                type="button"
                onClick={() => remove(it.name)}
                className="hover:text-red-400 shrink-0"
                aria-label={`${it.name} kaldır`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <Input
          data-testid={`${prefix}-search`}
          value={q}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          onChange={(e) => { setQ(e.target.value); setShowDropdown(true); }}
          placeholder={placeholder}
          className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 pl-9 h-9 text-xs"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 animate-spin" />
        )}

        {showDropdown && (
          <div
            className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-slate-950 border border-slate-700 rounded-sm shadow-lg"
            data-testid={`${prefix}-dropdown`}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-500">
                {q ? (
                  <>
                    "{q}" bulunamadı.
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setNewName(q); setAddOpen(true); }}
                      className="ml-2 text-emerald-400 hover:text-emerald-300 underline"
                    >
                      Yeni ekle
                    </button>
                  </>
                ) : "Kayıt yok"}
              </div>
            ) : (
              filtered.map((it) => {
                const isSelected = value.includes(it.name);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); toggle(it.name); }}
                    data-testid={`${prefix}-option-${it.name}`}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-800/50 last:border-0 transition-colors ${
                      isSelected ? "bg-emerald-950/40 text-emerald-100" : "hover:bg-slate-800/60 text-slate-200"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded-sm border shrink-0 flex items-center justify-center ${
                        isSelected ? "bg-emerald-500 border-emerald-500" : "border-slate-600 bg-slate-900"
                      }`}
                    >
                      {isSelected ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : <Plus className="w-3 h-3 text-slate-500" />}
                    </span>
                    <span className="text-slate-100 truncate flex-1">{it.name}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-sm sm:max-w-md bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>{label} — Yeni Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-slate-400">Ad</Label>
              <Input
                autoFocus
                data-testid={`${prefix}-new-input`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createNew(); }}
                placeholder="Örn: Fucan Pro"
                className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500"
              />
            </div>
            <div className="text-[11px] font-mono text-slate-500">
              Eklenen kayıt Tanımlar → {label} listesine gider ve otomatik seçilir.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button
              data-testid={`${prefix}-new-save`}
              onClick={createNew}
              disabled={saving || !newName.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
